// src/pages/QuizPage/components/QuizTopbar.tsx
import { useState, useEffect } from "react";
import { Heart, Clock, Minimize, Maximize, Plus, Minus } from "lucide-react";
import { applyTextScale } from "../../../utils/textScale";

const QUIZ_MIN_SCALE = 0.85;
const QUIZ_MAX_SCALE = 1.6;
const QUIZ_SCALE_KEY = "quizTextScale";

const getSavedQuizScale = (): number => {
  const saved = Number(localStorage.getItem(QUIZ_SCALE_KEY));
  return saved && saved >= QUIZ_MIN_SCALE && saved <= QUIZ_MAX_SCALE ? saved : 1;
};

type UiLang = "oz" | "uz" | "ru";

interface QuizTopbarProps {
  lang: UiLang;
  setLang: (lang: UiLang) => void;
  isMarafonMode: boolean;
  lives: number;
  hasTimer: boolean;
  timeLeft: number;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onFinish: () => void;
}

const fmt = (s: number) =>
  `${Math.floor(s / 60)
    .toString()
    .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

export default function QuizTopbar({
  lang,
  setLang,
  isMarafonMode,
  lives,
  hasTimer,
  timeLeft,
  isFullscreen,
  onToggleFullscreen,
  onFinish,
}: QuizTopbarProps) {
  // Quiz sahifasida global zoom 1x'ga tushirilgan (QuizPage/index.tsx).
  // Yoshi katta foydalanuvchilar uchun shu sahifada alohida
  // kattalashtirish/kichiklashtirish — faqat shu sahifa doirasida ishlaydi,
  // boshqa sahifalarga (Dashboard va h.k.) ta'sir qilmaydi.
  const [quizScale, setQuizScale] = useState(getSavedQuizScale);

  // Bilet/savol to'plami almashganda QuizTopbar qayta mount bo'ladi —
  // shu bois saqlangan qiymatni har mountda qayta qo'llaymiz, aks holda
  // QuizPage'ning standart 1x holati ustidan yozib ketaveradi.
  useEffect(() => {
    applyTextScale(quizScale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeQuizScale = (delta: number) => {
    setQuizScale((prev) => {
      const next = Math.min(
        QUIZ_MAX_SCALE,
        Math.max(QUIZ_MIN_SCALE, +(prev + delta).toFixed(2)),
      );
      localStorage.setItem(QUIZ_SCALE_KEY, String(next));
      applyTextScale(next);
      return next;
    });
  };

  return (
    <div className="w-full bg-black/40 border-b border-white/10 shrink-0">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        {/* Left: Logo */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 flex items-center justify-center">
            <img
              src="/logo.png"
              alt="AvtoMind"
              className="w-9 h-9 object-contain"
            />
          </div>
          <span className="font-bold text-white/90 hidden sm:block">
            AvtoMind
          </span>
        </div>

        {/* Center: Lang switcher */}
        <div className="flex items-center">
          {/* Mobile */}
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value as UiLang)}
            className="lg:hidden bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-bold text-white/90 outline-none">
            <option value="oz">O'zbekcha</option>
            <option value="uz">Ўзбекча</option>
            <option value="ru">Русский</option>
          </select>
          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
            {(["oz", "uz", "ru"] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1.0 rounded-lg text-sm font-bold transition ${
                  lang === l ?
                    "bg-blue-600 text-white"
                  : "text-white/50 hover:text-white/80"
                }`}>
                {l === "oz" ?
                  "O'zbekcha"
                : l === "uz" ?
                  "Ўзбекча"
                : "Русский"}
              </button>
            ))}
          </div>
        </div>

        {/* Right: lives + timer + yakunlash + fullscreen */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Marafon lives */}
          {isMarafonMode && (
            <div className="flex gap-1">
              {[...Array(3)].map((_, i) => (
                <Heart
                  key={i}
                  size={18}
                  fill={i < lives ? "#EF4444" : "none"}
                  className={i < lives ? "text-red-500" : "text-white/20"}
                />
              ))}
            </div>
          )}

          {/* Timer */}
          {hasTimer && (
            <div
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border font-mono font-bold text-sm ${
                timeLeft < 60 ?
                  "bg-red-500/15 border-red-500/40 text-red-300"
                : "bg-white/5 border-white/10 text-white"
              }`}>
              <Clock size={14} />
              {fmt(timeLeft)}
            </div>
          )}

          {/* Matn o'lchami — yoshi katta foydalanuvchilar uchun */}
          <div className="hidden sm:flex items-center border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => changeQuizScale(-0.1)}
              title="Kichraytirish"
              className="px-2 py-2 text-white/50 hover:text-white transition">
              <Minus size={14} />
            </button>
            <button
              onClick={() => changeQuizScale(0.1)}
              title="Kattalashtirish"
              className="px-2 py-2 border-l border-white/10 text-white/50 hover:text-white transition">
              <Plus size={14} />
            </button>
          </div>

          {/* Yakunlash */}
          <button
            onClick={onFinish}
            className="px-0.8 py-2.5 lg:px-4 lg:py-2 rounded-xl border border-red-500/25 bg-red-500/10 text-red-300 font-bold text-xs lg:text-sm hover:bg-red-500/20 transition whitespace-nowrap">
            Yakunlash
          </button>

          {/* Fullscreen — faqat desktop */}
          <button
            onClick={onToggleFullscreen}
            className="hidden lg:flex px-3 py-2 rounded-xl border border-white/10 bg-white/5 text-white/50 hover:text-white transition">
            {isFullscreen ?
              <Minimize size={14} />
            : <Maximize size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
